'use client'

import { useState, useRef, useEffect, ChangeEvent } from 'react'
import { message } from 'antd'
import { addUserFeedback } from 'Apis/feedback'
import {
  SettingsIconSelected,
  SettingsIconSelect,
  SettingsIconImage,
  SettingsUploadIconClose,
  IconSuccessful,
  IconFailed,
} from 'Icons'
import useI18n from 'Hooks/useI18n'
import CommonModal from '@/components/modal/CommonModal'
import {
  MAX_USER_UPLOAD_ANIMATED_SIZE,
  MAX_USER_UPLOAD_IMG_SIZE,
} from '@/constant/img'

const MAX_IMAGES = 8
const MAX_VIDEOS = 1
const MAX_CHARS = 950

type FeedbackMediaItem = {
  id: string
  file: File
  previewUrl: string
  type: 'image' | 'video'
}

type Props = {
  open: boolean
  onCancel: () => void
  onSuccess?: () => void
}

export default function FeedbackModal({ open, onCancel, onSuccess }: Props) {
  const { t } = useI18n()
  const [selectedType, setSelectedType] = useState('')
  const [feedbackText, setFeedbackText] = useState('')
  const [selectedMedia, setSelectedMedia] = useState<FeedbackMediaItem[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isFailure, setIsFailure] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const selectedMediaRef = useRef<FeedbackMediaItem[]>([])
  const imageCount = selectedMedia.filter(
    (mediaItem) => mediaItem.type === 'image'
  ).length
  const videoCount = selectedMedia.filter(
    (mediaItem) => mediaItem.type === 'video'
  ).length

  useEffect(() => {
    const adjustHeight = () => {
      const textarea = textareaRef.current
      if (textarea) {
        textarea.style.height = 'auto'
        textarea.style.height = `${textarea.scrollHeight}px`
      }
    }
    adjustHeight()
  }, [feedbackText])

  useEffect(() => {
    selectedMediaRef.current = selectedMedia
  }, [selectedMedia])

  useEffect(() => {
    return () => {
      selectedMediaRef.current.forEach((mediaItem) => {
        URL.revokeObjectURL(mediaItem.previewUrl)
      })
    }
  }, [])

  const revokeMediaPreview = (mediaItem: FeedbackMediaItem) => {
    URL.revokeObjectURL(mediaItem.previewUrl)
  }

  const createMediaItem = (
    file: File,
    index: number
  ): FeedbackMediaItem | null => {
    if (file.type.startsWith('image/')) {
      if (file.size > MAX_USER_UPLOAD_IMG_SIZE) {
        message.error(
          t('The uploaded image exceeds the maximum allowed size of 20MB.')
        )
        return null
      }
      return {
        id: `${file.name}-${file.lastModified}-${index}`,
        file,
        previewUrl: URL.createObjectURL(file),
        type: 'image',
      }
    }

    if (file.type.startsWith('video/')) {
      if (file.size > MAX_USER_UPLOAD_ANIMATED_SIZE) {
        message.error(
          t('The uploaded file exceeds the maximum allowed size of 20MB')
        )
        return null
      }
      return {
        id: `${file.name}-${file.lastModified}-${index}`,
        file,
        previewUrl: URL.createObjectURL(file),
        type: 'video',
      }
    }

    return null
  }

  const appendMediaItems = (files: FileList) => {
    const nextMedia = Array.from(files)
      .map((file, index) => createMediaItem(file, index))
      .filter(Boolean) as FeedbackMediaItem[]

    if (nextMedia.length === 0) return

    setSelectedMedia((prevMedia) => {
      let remainingImageSlots =
        MAX_IMAGES -
        prevMedia.filter((mediaItem) => mediaItem.type === 'image').length
      let remainingVideoSlots =
        MAX_VIDEOS -
        prevMedia.filter((mediaItem) => mediaItem.type === 'video').length
      const acceptedMedia: FeedbackMediaItem[] = []
      const rejectedMedia: FeedbackMediaItem[] = []

      nextMedia.forEach((mediaItem) => {
        if (mediaItem.type === 'image' && remainingImageSlots > 0) {
          acceptedMedia.push(mediaItem)
          remainingImageSlots -= 1
          return
        }

        if (mediaItem.type === 'video' && remainingVideoSlots > 0) {
          acceptedMedia.push(mediaItem)
          remainingVideoSlots -= 1
          return
        }

        rejectedMedia.push(mediaItem)
      })

      rejectedMedia.forEach((mediaItem) => {
        revokeMediaPreview(mediaItem)
      })

      return [...prevMedia, ...acceptedMedia]
    })
  }

  const openFilePicker = (accept: string) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = accept
    input.multiple = true
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files
      if (files) {
        appendMediaItems(files)
      }
    }
    input.click()
  }

  const handleImagePick = () => {
    openFilePicker('image/*')
  }

  const handleVideoPick = () => {
    openFilePicker('video/*')
  }

  const clearSelectedMedia = () => {
    selectedMediaRef.current.forEach((mediaItem) => {
      revokeMediaPreview(mediaItem)
    })
    setSelectedMedia([])
  }

  const handleRemoveMedia = (indexToRemove: number) => {
    setSelectedMedia((prevMedia) => {
      const mediaToRemove = prevMedia[indexToRemove]
      if (mediaToRemove) {
        revokeMediaPreview(mediaToRemove)
      }
      return prevMedia.filter((_, index) => index !== indexToRemove)
    })
  }

  const options = [
    {
      title: t('Billing & Gems'),
      description: t('Get Help with Top-ups, Payments and Gems'),
      type: 'billing_gems',
    },
    {
      title: t('Bug Report'),
      description: t('Found something broken or not working'),
      type: 'report',
    },
    {
      title: t('Suggestion'),
      description: t('Have ideas for enhancing the app'),
      type: 'suggest',
    },
    {
      title: t('Question'),
      description: t('Need help or have any inquiries'),
      type: 'question',
    },
    {
      title: t('Creator support'),
      description: t('Feedback on character creation features'),
      type: 'creator',
    },
  ]

  const handleOptionSelect = (type: string) => {
    setSelectedType(type)
  }

  const handleKeyDown = (e: React.KeyboardEvent, type: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleOptionSelect(type)
    }
  }

  const prepareFeedbackData = () => ({
    type: selectedType,
    content: feedbackText,
    images: selectedMedia
      .filter((mediaItem) => mediaItem.type === 'image')
      .map((mediaItem) => mediaItem.file),
    videos: selectedMedia
      .filter((mediaItem) => mediaItem.type === 'video')
      .map((mediaItem) => mediaItem.file),
  })

  const handleSubmit = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    setIsLoading(true)
    try {
      const feedbackData = prepareFeedbackData()
      await addUserFeedback(feedbackData)
      setSelectedType('')
      setFeedbackText('')
      clearSelectedMedia()
      setIsSuccess(true)
      setTimeout(() => {
        setIsSuccess(false)
        onSuccess?.()
        onCancel()
      }, 1500)
    } catch (error) {
      console.error('Error submitting feedback:', error)
      setIsFailure(true)
      setTimeout(() => setIsFailure(false), 1500)
    } finally {
      setIsSubmitting(false)
      setIsLoading(false)
    }
  }

  const handleFeedbackChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value
    if (text.length <= MAX_CHARS) {
      setFeedbackText(text)
    }
  }

  return (
    <CommonModal
      open={open}
      title={t('Feedback')}
      footer={null}
      isLoading={isLoading}
      onCancel={onCancel}
      width={500}
      destroyOnClose
    >
      <div className="relative px-2.5">
        <div className="mb-4 rounded-lg bg-white/10 p-4">
          {options.map((option, index) => (
            <div
              key={option.type}
              role="button"
              tabIndex={0}
              onClick={() => handleOptionSelect(option.type)}
              onKeyDown={(e) => handleKeyDown(e, option.type)}
              className={`flex cursor-pointer items-center justify-between py-2 ${index !== options.length - 1 ? 'border-b border-white/5' : ''}`}
            >
              <div>
                <div className="text-sm font-light text-white">
                  {option.title}
                </div>
                <div
                  className={`text-xs ${
                    selectedType === option.type
                      ? 'text-[#B3B3B3]'
                      : 'text-white/30'
                  }`}
                >
                  {option.description}
                </div>
              </div>
              <div className="flex h-5 w-5 items-center justify-center rounded-full">
                {selectedType === option.type ? (
                  <SettingsIconSelected />
                ) : (
                  <SettingsIconSelect />
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-lg bg-white/10 p-4">
          <textarea
            ref={textareaRef}
            placeholder={
              /* i18n-ignore */ selectedType === 'report' ||
              /* i18n-ignore */ selectedType === 'billing_gems'
                ? t(
                    'Please include screenshots/recordings and the Character CID (if applicable) for a faster resolution'
                  )
                : t('Let us know how we can help make your experience better')
            }
            value={feedbackText}
            onChange={handleFeedbackChange}
            maxLength={MAX_CHARS}
            className="min-h-[150px] w-full resize-none overflow-hidden rounded-lg bg-white/0 text-sm text-white placeholder-white/30 focus:border-transparent focus:outline-none focus:ring-0"
            style={{ WebkitAppearance: 'none' }}
          />
          {(imageCount < MAX_IMAGES || videoCount < MAX_VIDEOS) && (
            <>
              {imageCount < MAX_IMAGES && (
                <button
                  type="button"
                  onClick={handleImagePick}
                  className="flex h-[70px] w-[70px] flex-col items-center justify-center rounded-lg border border-dashed border-white/20"
                >
                  <SettingsIconImage />
                  <span className="mt-1 text-[9px] text-white/30">
                    {t('Image')}
                  </span>
                </button>
              )}
              {videoCount < MAX_VIDEOS && (
                <button
                  type="button"
                  onClick={handleVideoPick}
                  className="flex h-[70px] w-[70px] flex-col items-center justify-center rounded-lg border border-dashed border-white/20"
                >
                  <SettingsIconImage />
                  <span className="mt-1 text-[9px] text-white/30">
                    {t('Video')}
                  </span>
                </button>
              )}
            </>
          )}
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8">
            {selectedMedia.map((mediaItem, index) => (
              <div key={mediaItem.id} className="relative h-[70px] w-[70px]">
                {mediaItem.type === 'video' ? (
                  <>
                    <video
                      src={mediaItem.previewUrl}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      className="h-full w-full rounded-lg object-cover"
                    />
                    <span className="absolute bottom-1 left-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[9px] text-white">
                      {t('Video')}
                    </span>
                  </>
                ) : (
                  <img
                    src={mediaItem.previewUrl}
                    alt=""
                    className="h-full w-full rounded-lg object-cover"
                  />
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveMedia(index)}
                  className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full"
                  aria-label={t('Remove')}
                >
                  <SettingsUploadIconClose />
                </button>
              </div>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!selectedType || !feedbackText.trim() || isSubmitting}
          className={`mt-6 w-full rounded-full py-2 text-lg font-light ${
            !selectedType || !feedbackText.trim() || isSubmitting
              ? 'bg-white/10 text-white/30'
              : 'bg-[#9C4844] text-white'
          }`}
        >
          {t('Submit')}
        </button>

        {isSuccess && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-black/50">
            <div className="flex flex-col items-center justify-center rounded-3xl bg-gradient-to-r from-[rgba(67,46,60,0.9)] to-[rgba(61,44,41,0.9)] px-8 py-6">
              <div className="mb-4">
                <IconSuccessful />
              </div>
              <p className="whitespace-nowrap text-center text-sm text-white">
                {t('Submission Successful')}
              </p>
            </div>
          </div>
        )}

        {isFailure && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-black/50">
            <div className="flex flex-col items-center justify-center rounded-3xl bg-gradient-to-r from-[rgba(67,46,60,0.9)] to-[rgba(61,44,41,0.9)] px-8 py-6">
              <div className="mb-4">
                <IconFailed />
              </div>
              <p className="whitespace-nowrap text-center text-sm text-white">
                {t('Submission Failed')}
              </p>
            </div>
          </div>
        )}
      </div>
    </CommonModal>
  )
}
